/**
 * Comprime uma imagem no cliente antes do upload para evitar problemas com
 * proxies/limites de banda em redes móveis (4G/5G).
 *
 * - Reduz dimensão máxima (lado maior) preservando aspect ratio
 * - Re-encoda como JPEG com qualidade configurável
 * - Mantém EXIF orientation via CanvasRenderingContext2D (auto-orient padrão)
 *
 * Tipicamente reduz fotos de 8-15MB para 200-500KB.
 *
 * @param {File} file
 * @param {{ maxDim?: number, quality?: number }} opts
 * @returns {Promise<File>}
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.85 } = {}) {
  // Não comprime se já for pequeno (<300KB) ou não for imagem
  if (!file || !file.type?.startsWith("image/")) return file;
  if (file.size < 300 * 1024) {
    // Marca o original com info de compressão (skip)
    file._compressionInfo = { originalSize: file.size, finalSize: file.size, skipped: true };
    return file;
  }

  return new Promise((resolve, reject) => {
    const originalSize = file.size;
    const reader = new FileReader();
    const img = new Image();
    reader.onerror = () => reject(new Error("Falha ao ler arquivo"));
    img.onerror = () => reject(new Error("Falha ao decodificar imagem"));
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Falha ao gerar JPEG"));
          const fname = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
          const compressed = new File([blob], fname, { type: "image/jpeg" });
          compressed._compressionInfo = {
            originalSize,
            finalSize: blob.size,
            skipped: false,
            savedPct: Math.round(((originalSize - blob.size) / originalSize) * 100),
          };
          resolve(compressed);
        },
        "image/jpeg",
        quality,
      );
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Formata um tamanho em bytes para human-readable (KB / MB).
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
